import * as React from 'react';
import {connect} from 'react-redux';

import * as ramda from 'ramda';
import _ from 'underscore';

import {Assignables} from 'nutshell-core/assignables';
import type {Assignable} from 'nutshell-core/types';

import {AssigneeSelect} from 'shells/assignee-select/assignee-select';
import {CrossBrowserHiddenFormSubmit} from 'shells/form/cross-browser-hidden-form-submit';
import {Label} from 'shells/form/label';
import {TextField} from 'shells/textfield/textfield';

import type {
    DeleteStepTemplateInputFieldInput,
    GetStepTemplate as GetStepTemplateQuery,
    StepDueDateInput,
} from 'nutshell-graphql-types';

import type {NutshellState} from '../../../store';

import {SalesAutomationSidebarFormWrapper} from '../../core/sidebar/sales-automation-sidebar-form-wrapper';

import {DateTimePicker} from './date-time-picker';

import styles from './sales-process-form.module.css';

function mapStateToProps(state: NutshellState): StateProps {
    return {
        // @ts-expect-error
        assignables: Assignables.getAllEnabled(state),
    };
}

export type TodoTemplateInput = {
    name: string;
    description: string | null | undefined;
    dueDate: StepDueDateInput | null | undefined;
    assigneeId: string | null | undefined;
};

type OwnProps = {
    todoTemplate?: GetStepTemplateQuery['stepTemplate'] | null | undefined;
    formSubmitBtnRef: (arg1?: HTMLInputElement | null | undefined) => void;
    isFetchingLatestVersion: boolean;
    onSubmit: (arg1: TodoTemplateInput) => Promise<any>;
    onDelete: (arg1: DeleteStepTemplateInputFieldInput) => Promise<any>;
};

type StateProps = {
    assignables: Assignable[];
};

type State = {
    name: string;
    description: string;
    assigneeId: string | null | undefined;
    dueDate: StepDueDateInput | null | undefined;
};

type Props = OwnProps & StateProps;

export class TodoTemplateFormComponent extends React.PureComponent<Props, State> {
    nameTextFieldRef: HTMLInputElement | null | undefined;

    constructor(props: Props) {
        super(props);

        this.state = getFormStateFromTodoTemplate(props.todoTemplate);
    }

    componentDidMount() {
        // Using defer to ensure text is filled before it is selected
        // Without this, text will not be selected properly on first mount
        _.defer(() => {
            if (this.nameTextFieldRef) {
                this.nameTextFieldRef.focus();
            }
        });
    }

    static getDerivedStateFromProps(props: Props, state: State) {
        if (state.name || state.description || state.assigneeId || state.dueDate) return null;

        return getFormStateFromTodoTemplate(props.todoTemplate);
    }

    render() {
        return (
            <SalesAutomationSidebarFormWrapper
                id={this.props.todoTemplate ? this.props.todoTemplate.id : null}
                isFetchingLatestVersion={this.props.isFetchingLatestVersion}
                onSaveForm={this.saveForm}
                onDelete={{
                    deleteText: 'Remove task',
                    deleteConfirmationTitle: 'Are you sure you want to remove this task?',
                    onDeleteConfirm: this.handleDeleteConfirm,
                }}
            >
                <div className={styles['form-input']}>
                    <Label required={true} htmlFor='TitleTextField'>
                        Title
                    </Label>
                    <TextField
                        // @ts-expect-error
                        name='taskName'
                        id='TitleTextField'
                        required={true}
                        disabled={this.props.isFetchingLatestVersion}
                        placeholder='Example: Prepare demo materials'
                        textFieldRef={(c) => {
                            this.nameTextFieldRef = c;
                        }}
                        onFocus={this.handleFocus}
                        value={this.state.name}
                        onChange={this.handleNameChange}
                    />
                </div>
                <div className={styles['form-input']}>
                    <Label htmlFor='AssigneePicker'>Assignee</Label>
                    <AssigneeSelect
                        inputId='AssigneePicker'
                        placeholder='Assigned to lead owner'
                        isDisabled={this.props.isFetchingLatestVersion}
                        onChange={this.handleAssigneeChange}
                        value={this.state.assigneeId}
                        options={this.props.assignables}
                    />
                </div>
                <div className={styles['form-input']}>
                    <DateTimePicker
                        disabled={this.props.isFetchingLatestVersion}
                        dueDate={this.state.dueDate}
                        onDueDateChange={this.handleDueDateChange}
                    />
                </div>
                <div className={styles['form-input']}>
                    <Label htmlFor='DescriptionTextField'>Description</Label>
                    <TextField
                        // @ts-expect-error
                        name='description'
                        id='DescriptionTextField'
                        placeholder='Add a note or helpful sales coaching tips'
                        disabled={this.props.isFetchingLatestVersion}
                        value={this.state.description}
                        multiline={true}
                        onChange={this.handleDescriptionChange}
                    />
                </div>
                <CrossBrowserHiddenFormSubmit inputRef={this.props.formSubmitBtnRef} />
            </SalesAutomationSidebarFormWrapper>
        );
    }

    handleDueDateChange = (newValue?: StepDueDateInput | null) => {
        this.setState({
            dueDate: newValue,
        });
    };

    // This is for selecting the entire input field
    handleFocus = (e: React.ChangeEvent<HTMLInputElement>) => {
        e.target.select();
    };

    handleNameChange = (val: string) => {
        this.setState({name: val});
    };

    handleDescriptionChange = (val: string) => {
        this.setState({description: val});
    };

    handleAssigneeChange = (newAssigneeId?: string | null) => {
        this.setState({assigneeId: newAssigneeId});
    };

    handleDeleteConfirm = (entityId: string) => {
        return this.props.onDelete({
            id: entityId,
        });
    };

    saveForm = () => {
        return this.props.onSubmit({
            name: this.state.name,
            description: this.state.description || '',
            assigneeId: this.state.assigneeId,
            dueDate: this.state.dueDate,
        });
    };
}

function getFormStateFromTodoTemplate(todoTemplate?: GetStepTemplateQuery['stepTemplate'] | null) {
    return {
        name: todoTemplate ? todoTemplate.name : '',
        description: todoTemplate && todoTemplate.description ? todoTemplate.description : '',
        assigneeId: todoTemplate && todoTemplate.assignee ? todoTemplate.assignee.id : null,
        dueDate:
            todoTemplate && todoTemplate.dueDate
                ? ramda.omit(['__typename'], todoTemplate.dueDate)
                : null,
    };
}

// @ts-expect-error
const connector = connect<Props, OwnProps, _, _, _, _>(mapStateToProps);
export const TodoTemplateForm = connector(TodoTemplateFormComponent);
